<script lang="ts" setup>
// ============================================================================
// SourceDataGrdfView (DEV-595, P1) — page de DONNÉES dédiée PCE GRDF, assemblée.
// ----------------------------------------------------------------------------
// Vue présentationnelle (data via props → alimentée par la route dataV2). Assemble :
//   • un sélecteur de PÉRIODE LIBRE (calendrier range maison + raccourcis)
//   • un sélecteur de VUE DE DONNÉES (Facturation publié ⇄ Suivi quotidien
//     informatif) — les 2 représentations complémentaires de la conso GRDF
//   • un toggle Tableau ⇄ Graphe (défaut = Graphe)
//   • Tableau = relevés bruts (C1) · Graphe = 1 chart par groupe d'unité (C3)
// 100 % données brutes (pas de coût/calculé) — cf. DEV-595.
//
// IMPORTANT : le composant reste MONTÉ pendant les rechargements (prop `loading`
// → overlay, pas de v-if destructeur côté page) pour que l'état (vue, sélecteurs,
// toggle) PERSISTE quand on change de période ou de vue.
// ============================================================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { CalendarState } from "primevue/calendar"
import DataRawReadingsTable, {
    type RawReading,
} from "../table/DataRawReadingsTable.vue"
import RawSeriesChart, { type RawSeries } from "../chart/RawSeriesChart.vue"

/** Un groupe de séries homogènes (même unité) → 1 graphe. */
export type ChartGroup = { title: string; series: RawSeries[] }

/** Une vue de données (ex. Facturation = periode/publié · Suivi = daily/informatif). */
export type DataSourceView = {
    key: string
    label: string
    readings: RawReading[]
    chartGroups: ChartGroup[]
}

const props = withDefaults(
    defineProps<{
        pceNumber: string
        /** Fenêtre courante [début, fin] (drive l'appel dapi côté page). */
        period: [Date, Date]
        /** Vues de données complémentaires (Facturation / Suivi). Si fourni →
         *  sélecteur de vue. Sinon on retombe sur `readings`/`chartGroups`. */
        sources?: DataSourceView[]
        /** Mono-source (rétro-compat) : relevés + groupes directs. */
        readings?: RawReading[]
        chartGroups?: ChartGroup[]
        /** vue initiale tableau/graphe — défaut GRAPHE. */
        view?: "table" | "chart"
        /** rechargement en cours → overlay (le composant reste monté). */
        loading?: boolean
    }>(),
    {
        view: "chart",
        loading: false,
        sources: undefined,
        readings: undefined,
        chartGroups: undefined,
    }
)

const emit = defineEmits<{
    (e: "update:period", payload: [Date, Date]): void
}>()

const { t } = useI18n({
    messages: {
        fr: {
            title: "Données du compteur",
            subtitle: "Données brutes remontées par le connecteur GRDF",
            period: "Période",
            dataView: "Vue",
            table: "Tableau",
            chart: "Graphe",
            loading: "Chargement des données…",
            shortcuts: { m1: "1 mois", m3: "3 mois", m6: "6 mois", y1: "1 an" },
        },
    },
})

const view = ref<"table" | "chart">(props.view)

// --- Sélecteur de période LIBRE : calendrier range maison + raccourcis -------
const today = new Date()
const periodPicker = ref<CalendarState>({} as CalendarState)
/** Période locale : la sélection range passe par [start, null] avant [start, end]. */
const localPeriod = ref<[Date, Date | null]>([props.period[0], props.period[1]])
watch(
    () => props.period,
    (p) => {
        localPeriod.value = [p[0], p[1]]
    }
)
watch(localPeriod, (p) => {
    if (!p[0] || !p[1]) return // sélection incomplète → on attend la 2ᵉ borne
    const sameStart = p[0].getTime() === props.period[0]?.getTime()
    const sameEnd = p[1].getTime() === props.period[1]?.getTime()
    if (sameStart && sameEnd) return // pas de ré-émission identique (anti-boucle)
    emit("update:period", [p[0], p[1]])
})

const SHORTCUTS = [
    { key: "m1", months: 1, labelKey: "shortcuts.m1" },
    { key: "m3", months: 3, labelKey: "shortcuts.m3" },
    { key: "m6", months: 6, labelKey: "shortcuts.m6" },
    { key: "y1", months: 12, labelKey: "shortcuts.y1" },
] as const
function applyShortcut(months: number): void {
    const to = new Date()
    const from = new Date()
    from.setMonth(from.getMonth() - months)
    localPeriod.value = [from, to]
    if (periodPicker.value) periodPicker.value.overlayVisible = false
}

// --- Vue de données (Facturation / Suivi) ----------------------------------
const hasSources = computed<boolean>(
    () => Array.isArray(props.sources) && props.sources.length > 1
)
const sourceOptions = computed(() =>
    (props.sources ?? []).map((s) => ({ name: s.label, value: s.key }))
)
const selectedSourceKey = ref<string>(props.sources?.[0]?.key ?? "")
// Initialise la vue sélectionnée dès que les sources arrivent (1er load) ;
// préservée ensuite (les clés restent stables entre refetch → pas de reset).
watch(
    () => props.sources,
    (s) => {
        if (s?.length && !s.some((x) => x.key === selectedSourceKey.value)) {
            selectedSourceKey.value = s[0].key
        }
    },
    { immediate: true }
)

const active = computed<{ readings: RawReading[]; chartGroups: ChartGroup[] }>(
    () => {
        if (props.sources?.length) {
            const found =
                props.sources.find((s) => s.key === selectedSourceKey.value) ??
                props.sources[0]
            return { readings: found.readings, chartGroups: found.chartGroups }
        }
        return {
            readings: props.readings ?? [],
            chartGroups: props.chartGroups ?? [],
        }
    }
)
</script>

<template>
    <div class="source-data-grdf-view flex flex-col gap-4">
        <!-- En-tête -->
        <header class="flex flex-col gap-1">
            <h2 class="text-xl font-semibold text-slate-800">
                {{ t("title") }}
            </h2>
            <p class="text-sm text-slate-600">
                PCE {{ pceNumber }} · {{ t("subtitle") }}
            </p>
        </header>

        <!-- Barre : période (libre) + vue de données + toggle table/graphe -->
        <div class="flex flex-wrap items-end justify-between gap-3">
            <div class="flex flex-wrap items-end gap-3">
                <!-- Sélecteur de période LIBRE (calendrier range + raccourcis) -->
                <BaseFloatLabel variant="on" class="min-w-80">
                    <BaseDatePicker
                        input-id="grdf-period"
                        :aria-label="t('period')"
                        ref="periodPicker"
                        class="w-full"
                        show-icon
                        selection-mode="range"
                        date-format="dd/mm/yy"
                        :manual-input="false"
                        v-model="localPeriod"
                        :max-date="today"
                        hide-on-range-selection
                        panel-class="min-w-[392px]"
                    >
                        <template #footer>
                            <div
                                class="grid grid-cols-4 gap-1 pt-1"
                                role="group"
                                aria-label="Raccourcis de période"
                            >
                                <BaseButton
                                    v-for="s in SHORTCUTS"
                                    :key="s.key"
                                    size="small"
                                    outlined
                                    severity="secondary"
                                    :label="t(s.labelKey)"
                                    @click="applyShortcut(s.months)"
                                />
                            </div>
                        </template>
                    </BaseDatePicker>
                    <label for="grdf-period">{{ t("period") }}</label>
                </BaseFloatLabel>

                <!-- Sélecteur Facturation / Suivi quotidien (pattern maison) -->
                <BaseFloatLabel v-if="hasSources" variant="on" class="min-w-52">
                    <BaseSelect
                        id="grdf-data-view"
                        class="w-full"
                        v-model="selectedSourceKey"
                        :options="sourceOptions"
                        option-label="name"
                        option-value="value"
                    />
                    <label for="grdf-data-view">{{ t("dataView") }}</label>
                </BaseFloatLabel>
            </div>

            <!-- Toggle Tableau / Graphe -->
            <div
                class="inline-flex rounded-lg bg-slate-100 p-0.5"
                role="group"
                :aria-label="`${t('table')} / ${t('chart')}`"
            >
                <button
                    type="button"
                    class="rounded-md px-3 py-1 text-sm font-medium transition"
                    :class="
                        view === 'table'
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-700 hover:text-slate-900'
                    "
                    :aria-pressed="view === 'table'"
                    @click="view = 'table'"
                >
                    {{ t("table") }}
                </button>
                <button
                    type="button"
                    class="rounded-md px-3 py-1 text-sm font-medium transition"
                    :class="
                        view === 'chart'
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-700 hover:text-slate-900'
                    "
                    :aria-pressed="view === 'chart'"
                    @click="view = 'chart'"
                >
                    {{ t("chart") }}
                </button>
            </div>
        </div>

        <!-- Zone de contenu : reste MONTÉE pendant les refetch (overlay loading) -->
        <div class="relative min-h-[220px]">
            <div
                v-if="loading"
                class="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-lg bg-white/60"
                role="status"
                aria-live="polite"
            >
                <i
                    class="pi pi-spin pi-spinner text-2xl text-slate-400"
                    aria-hidden="true"
                />
                <span class="sr-only">{{ t("loading") }}</span>
            </div>

            <!-- Vue TABLEAU : relevés bruts (chaîne de mesure + qualité couleur) -->
            <DataRawReadingsTable
                v-if="view === 'table'"
                :readings="active.readings"
            />

            <!-- Vue GRAPHE : 1 chart par groupe d'unité -->
            <div v-else class="flex flex-col gap-6">
                <section
                    v-for="group in active.chartGroups"
                    :key="group.title"
                    class="flex flex-col gap-2"
                >
                    <h3 class="text-sm font-semibold text-slate-700">
                        {{ group.title }}
                    </h3>
                    <RawSeriesChart :series="group.series" />
                </section>
            </div>
        </div>
    </div>
</template>
